import { z } from 'zod'
import { publicIdSchema, utcTimestampSchema } from './primitives'

export const SCORE_TYPE_VALUES = ['numeric', 'scale', 'boolean', 'select'] as const
export const scoreTypeSchema = z.enum(SCORE_TYPE_VALUES)
export type ScoreType = z.infer<typeof scoreTypeSchema>

/** One band of guidance shown to judges, e.g. "9-10: outstanding". */
export const rubricAnchorSchema = z
  .strictObject({
    min: z.number().finite(),
    max: z.number().finite(),
    description: z.string().trim().min(1).max(500),
  })
  .refine((anchor) => anchor.min <= anchor.max, 'An anchor requires min <= max.')
export type RubricAnchor = z.infer<typeof rubricAnchorSchema>

export const rubricItemInputSchema = z
  .strictObject({
    name: z.string().trim().min(1).max(200),
    description: z.string().max(2000).nullable().default(null),
    scoreType: scoreTypeSchema,
    minScore: z.number().finite().nullable().default(null),
    maxScore: z.number().finite().nullable().default(null),
    weight: z.number().finite().min(0).max(100),
    sortOrder: z.int().min(0).max(10_000),
    required: z.boolean().default(true),
    options: z.array(z.string().trim().min(1).max(200)).max(100).nullable().default(null),
    /** Optional scoring bands shown to judges; absent or null means none. */
    anchors: z.array(rubricAnchorSchema).max(12).nullable().optional(),
  })
  .superRefine((item, ctx) => {
    if (
      (item.scoreType === 'numeric' || item.scoreType === 'scale') &&
      (item.minScore === null || item.maxScore === null || item.minScore > item.maxScore)
    )
      ctx.addIssue({
        code: 'custom',
        message: 'Numeric and scale items require minScore <= maxScore.',
      })
    if (item.scoreType === 'select' && (item.options === null || item.options.length === 0))
      ctx.addIssue({ code: 'custom', message: 'Select items require options.' })
  })
export type RubricItemInput = z.infer<typeof rubricItemInputSchema>

const rubricWrite = {
  name: z.string().trim().min(1).max(200),
  description: z.string().max(2000).nullable().default(null),
  items: z.array(rubricItemInputSchema).min(1).max(100),
}
export const createRubricRequestSchema = z.strictObject(rubricWrite)
export type CreateRubricRequest = z.infer<typeof createRubricRequestSchema>
export const replaceRubricItemsRequestSchema = z.strictObject({ items: rubricWrite.items })
export type ReplaceRubricItemsRequest = z.infer<typeof replaceRubricItemsRequestSchema>

export const rubricItemDtoSchema = rubricItemInputSchema.extend({
  id: publicIdSchema,
  createdAt: utcTimestampSchema,
})
export type RubricItemDto = z.infer<typeof rubricItemDtoSchema>
export const rubricDtoSchema = z.object({
  id: publicIdSchema,
  name: z.string(),
  description: z.string().nullable(),
  version: z.int(),
  latestVersionId: publicIdSchema,
  /** Admin-set display name for `latestVersionId` (e.g. "初賽"), null until set. */
  latestVersionLabel: z.string().nullable(),
  status: z.enum(['active', 'archived']),
  createdAt: utcTimestampSchema,
  items: z.array(rubricItemDtoSchema),
})
export type RubricDto = z.infer<typeof rubricDtoSchema>

export const setRubricVersionLabelRequestSchema = z.strictObject({
  label: z.string().trim().min(1).max(100).nullable(),
})
export type SetRubricVersionLabelRequest = z.infer<typeof setRubricVersionLabelRequestSchema>
